/**
 * Logika nagród/medali wyścigu pływackiego (§14.5 + MINIGAMES.swimrace).
 * Czyste funkcje — bez Phaser.
 */
import { MINIGAMES } from '@pieskowo/shared';

export type RaceDistance = 1 | 2 | 3;

/** Długość basenu w umownych jednostkach pozycji (= 25 „m"). */
export const POOL_LENGTH_UNITS = 25;

export const DISTANCE_OPTIONS: readonly {
  lengths: RaceDistance;
  meters: number;
  labelPl: string;
}[] = [
  { lengths: 1, meters: 25, labelPl: '1 długość (25 m)' },
  { lengths: 2, meters: 50, labelPl: '2 długości (50 m)' },
  { lengths: 3, meters: 75, labelPl: '3 długości (75 m)' },
] as const;

export type MedalKind = 'gold' | 'silver' | 'smile';

export type RaceResult = {
  place: 1 | 2 | 3;
  medal: MedalKind;
  medalLabelPl: string;
  stars: number;
  score: number;
  /** „Dobra gra" wg §14.5: ≥2 długości LUB 1. miejsce. */
  goodGame: boolean;
  finishedLengths: RaceDistance;
};

export function trackLengthUnits(lengths: RaceDistance): number {
  return lengths * POOL_LENGTH_UNITS;
}

export function medalForPlace(place: 1 | 2 | 3): {
  medal: MedalKind;
  labelPl: string;
} {
  if (place === 1) return { medal: 'gold', labelPl: 'Złoty medal!' };
  if (place === 2) return { medal: 'silver', labelPl: 'Srebrny medal!' };
  return { medal: 'smile', labelPl: 'Dobra próba!' };
}

/**
 * Miejsce gracza vs 2 NPC (pos w jednostkach toru).
 * Remisy: gracz wygrywa remis (przyjazne dla dziecka).
 */
export function computePlace(
  playerPos: number,
  npcPositions: readonly number[],
): 1 | 2 | 3 {
  let better = 0;
  for (const n of npcPositions) {
    if (n > playerPos) better += 1;
  }
  const place = (better + 1) as 1 | 2 | 3;
  return place > 3 ? 3 : place;
}

/**
 * Gwiazdki (max 5 z katalogu mini-gier):
 * +1 ukończenie, +1 za dystans≥2, +1 za dystans=3, +1 za 1. miejsce, +1 za tempo.
 */
export function computeStars(input: {
  lengths: RaceDistance;
  place: 1 | 2 | 3;
  taps: number;
  durationMs: number;
}): number {
  const max = MINIGAMES.swimrace.maxStars;
  let stars = 1; // zawsze ukończenie (brak przegranej)
  if (input.lengths >= 2) stars += 1;
  if (input.lengths >= 3) stars += 1;
  if (input.place === 1) stars += 1;
  const sec = Math.max(input.durationMs / 1000, 0.001);
  const tapsPerS = input.taps / sec;
  // solidne tempo (ok. połowa client capa) → gwiazdka
  if (tapsPerS >= 6) stars += 1;
  return Math.min(max, stars);
}

export function evaluateRace(input: {
  lengths: RaceDistance;
  playerPos: number;
  npcPositions: readonly number[];
  taps: number;
  durationMs: number;
}): RaceResult {
  const place = computePlace(input.playerPos, input.npcPositions);
  const { medal, labelPl } = medalForPlace(place);
  const stars = computeStars({
    lengths: input.lengths,
    place,
    taps: input.taps,
    durationMs: input.durationMs,
  });
  const goodGame = input.lengths >= 2 || place === 1;
  // score: postęp + bonus miejsca + tapy (informacyjne)
  const score = Math.max(
    0,
    Math.floor(input.playerPos * 10 + (4 - place) * 50 + input.taps),
  );
  return {
    place,
    medal,
    medalLabelPl: labelPl,
    stars,
    score,
    goodGame,
    finishedLengths: input.lengths,
  };
}

/** Bazowa prędkość NPC (łagodna krzywa + faza) — jednostki/s w umownych metrach. */
export function npcBaseSpeed(npcIndex: number, elapsedSec: number): number {
  const base = 2.2 + npcIndex * 0.15;
  const wave = Math.sin(elapsedSec * (0.7 + npcIndex * 0.2) + npcIndex) * 0.35;
  return Math.max(0.4, base + wave);
}
